import type { CollectionRelation } from '@rstore/shared';
import type { MaybeRefOrGetter } from 'vue';
import type { GlobalCollectionId, GlobalDataSourceId, GlobalFieldId, GlobalPrimitiveFieldId, GlobalSingleRelationFieldId, PrimitiveType, ResolvedMonospaceCollection, ResolvedMonospaceDataSource, ResolvedMonospacePrimitiveField, ResolvedMonospaceSingleRelationField } from '#layers/data-model/public';
import type { CustomCollectionMeta } from '#shared/types/rstore';
import { normalizeCollectionRelations, resolveCollectionOppositeRelations } from '@rstore/core';
import { addCollection, removeCollection } from '@rstore/vue';
import { createEventHook } from '@vueuse/core';
import { defineStore } from 'pinia';
import { computed, ref, toValue } from 'vue';
import { defineCollection, useStore } from '#imports';
import { getCollectionPrimaryKeyFields, getRelationPrimitiveFields, getTypename, isPrimitiveTypeUnsupported, pickByPrimitiveFields, resolveFieldParser, stringifyPrimaryKey } from '#layers/data-model/public';
import { useWorkspaceStore } from './workspace';

// TODO tests for the utility functions

export const useWorkspaceSchemaStore = defineStore('workspace-schema', () => {
	const store = useStore();
	const { currentWorkspaceSlug, onWorkspaceChanged } = useWorkspaceStore();

	const { data: volatileSources, loading: isHydrating, error, refresh: _refresh } = store.MonospaceDataSource.query(
		(q) => q.many(
			currentWorkspaceSlug.value !== null
				? {
						include: {
							namespaces: true,
							collections: {
								primitiveFields: {
									meta: true,
								},
								relationFields: {
									constraint: {
										fieldMappings: true,
									},
									meta: true,
								},
								indexes: {
									fields: true,
								},
								meta: true,
							},
						},
						workspaceSlug: currentWorkspaceSlug.value,
						params: {
							deep: {
								collections: {
									// TODO expose the maximum allowed limit by the engine (set as query_limit_max in config)
									// TODO pagination? count?
									limit: 1000,
								},
							},
						},
					}
				: { enabled: false },
		),
	);

	const dataSources = ref<ResolvedMonospaceDataSource[]>([]);

	const allCollections = computed(() => dataSources.value?.flatMap((source) => source.collections) ?? []);
	const allPrimitiveFields = computed(() => allCollections.value.flatMap((collection) => collection.primitiveFields));
	const allSingleRelationFields = computed(() => allCollections.value.flatMap((collection) => collection.relationFields));

	const collectionsById = computed(() => Object.fromEntries(allCollections.value.map((collection) => [collection.id, collection])));
	const primitiveFieldsById = computed(() => Object.fromEntries(allPrimitiveFields.value.map((field) => [field.id, field])));
	const singleRelationFieldsById = computed(() => Object.fromEntries(allSingleRelationFields.value.map((field) => [field.id, field])));

	const beforeHydrateHook = createEventHook();
	const refreshedHook = createEventHook();

	onWorkspaceChanged(async ({ newWorkspaceId, oldWorkspaceId }) => {
		if (newWorkspaceId) {
			await refresh(newWorkspaceId !== oldWorkspaceId);
		}
		else {
			dataSources.value = [];
			cleanup([]);
		}
	});

	return {
		dataSources: computed(() => dataSources.value),
		collections: allCollections,
		nonSystemCollections: computed(() => allCollections.value.filter(({ isSystem }) => !isSystem)),
		primitiveFields: allPrimitiveFields,

		getCollection,
		findFieldById,
		findFieldByName,

		findSourceById,
		findCollectionById,
		findPrimitiveFieldById,
		findPrimitiveFieldByName,
		findCollectionByName,
		findSingleRelationFieldById,
		findSingleRelationFieldByName,

		isHydrating,
		refresh,
		error,

		onBeforeHydrate: beforeHydrateHook.on,
		onRefreshed: refreshedHook.on,
	};

	async function refresh(initialWorkspaceHydration = false) {
		if (initialWorkspaceHydration) {
			clearWorkspaceCaches();
		}

		await _refresh();

		const sources = volatileSources.value;

		// Guard: don't wipe state on fetch failure.
		// Check sources directly — error.value flush timing from rstore is not guaranteed
		// to be synchronous with _refresh() resolution.
		if (sources.length === 0 && initialWorkspaceHydration) return;

		// INVARIANT: Nothing between this line and hydrate() may clear rstore caches.
		// `sources` and `dataSources.value` hold the same reactive proxy graph from rstore.
		// Cache clearing would hollow out these proxies.
		dataSources.value = sources;

		await beforeHydrateHook.trigger();
		await hydrate(sources);
		await refreshedHook.trigger();
	}

	function findSourceById(sourceId: GlobalDataSourceId): ResolvedMonospaceDataSource | undefined {
		return dataSources.value?.find(({ id }) => id === sourceId);
	}

	function findCollectionById(collectionId: GlobalCollectionId): ResolvedMonospaceCollection | undefined {
		return collectionsById.value[collectionId];
	}

	function findCollectionByName(collectionName: string): ResolvedMonospaceCollection | undefined {
		return allCollections.value.find((collection) => collection.apiName === collectionName);
	}

	function findPrimitiveFieldById(fieldId: GlobalPrimitiveFieldId): ResolvedMonospacePrimitiveField | undefined {
		return primitiveFieldsById.value[fieldId];
	}

	function findPrimitiveFieldByName(collection: MaybeRefOrGetter<ResolvedMonospaceCollection>, fieldName: string): ResolvedMonospacePrimitiveField | undefined {
		return toValue(allPrimitiveFields).find((field) => {
			return field.apiName === fieldName && field.collection?.id === toValue(collection).id;
		});
	}

	function findFieldByName(collection: MaybeRefOrGetter<ResolvedMonospaceCollection>, fieldName: string) {
		const relationField = findSingleRelationFieldByName(collection, fieldName);
		const primitiveField = findPrimitiveFieldByName(collection, fieldName);
		return relationField ?? primitiveField;
	}

	function findSingleRelationFieldById(fieldId: GlobalSingleRelationFieldId): ResolvedMonospaceSingleRelationField | undefined {
		return singleRelationFieldsById.value[fieldId];
	}

	function findFieldById(fieldId: GlobalFieldId) {
		const primitiveField = findPrimitiveFieldById(fieldId as GlobalPrimitiveFieldId);
		const relationField = findSingleRelationFieldById(fieldId as GlobalSingleRelationFieldId);
		return relationField ?? primitiveField;
	}

	function findSingleRelationFieldByName(collection: MaybeRefOrGetter<ResolvedMonospaceCollection>, fieldName: string, fields: MaybeRefOrGetter<ResolvedMonospaceSingleRelationField[]> = allSingleRelationFields): ResolvedMonospaceSingleRelationField | undefined {
		return toValue(fields).find((field) => {
			return field.apiName === fieldName && field.collection?.id === toValue(collection).id;
		});
	}

	function getCollection(collectionOrId: GlobalCollectionId | ResolvedMonospaceCollection): ResolvedMonospaceCollection | undefined {
		return typeof collectionOrId === 'string' ? findCollectionById(collectionOrId) : collectionOrId;
	}

	async function hydrate(sources: ResolvedMonospaceDataSource[]) {
		const collections = sources.flatMap((s) => s.collections);
		cleanup(collections);

		let patchedRelations = false;

		for (const monospaceCollection of collections) {
			// Field config

			const fieldParsers = Object.fromEntries(
				monospaceCollection.primitiveFields
					.map((field) => (
						[field.apiName, resolveFieldParser(getTypename(field.type))]
					))
					.filter(([_, parser]) => !!parser),
			);

			const primitiveFieldTypes: Record<string, PrimitiveType> = Object.fromEntries(
				monospaceCollection.primitiveFields.map((field) => [field.apiName, field.type]),
			);

			// Metadata

			const relationsWithConstraint = new Set<string>();

			for (const relationField of monospaceCollection.relationFields) {
				// Checking if the relation field has a constraint
				// should be enough to determine if it is the side
				// that contains the primitive field of the relation
				if (relationField.constraintId) {
					relationsWithConstraint.add(relationField.apiName);
				}
			}

			const meta: CustomCollectionMeta = {
				defaultFields: monospaceCollection.primitiveFields
					.filter((field) => !isPrimitiveTypeUnsupported(field.type))
					.map((field) => field.apiName),
				useWorkspaceSlug: true,
				fromWorkspaceCollections: true,
				relationsWithConstraint,
				primitiveFieldTypes,
			};

			let getKey: ((item: any) => string) | undefined;

			const relations: Record<string, CollectionRelation> = {};

			if (!monospaceCollection.isSystem) {
				const primaryKeys = getCollectionPrimaryKeyFields(monospaceCollection);
				const isComposite = meta.isKeyComposite = primaryKeys.length > 1;

				if (isComposite) {
					getKey = (item: any) => {
						return stringifyPrimaryKey(pickByPrimitiveFields(item, primaryKeys));
					};
				}
				else {
					const primaryKey = primaryKeys[0]!;

					getKey = (item: any) => item[primaryKey.apiName];
				}
			}

			// Generate relations for workspace collections

			if (!monospaceCollection.isSystem) {
				try {
					for (const relationField of monospaceCollection.relationFields) {
						const oppositeCollection = relationField.oppositeCollection;

						if (!oppositeCollection) {
							throw new Error(`Opposite collection ${relationField.oppositeCollectionId} not found for relation field ${relationField.apiName}`);
						}

						const { fields, oppositeFields } = getRelationPrimitiveFields(relationField);

						if (fields.length === 0) {
							throw new Error(`No primitive field found for relation field ${relationField.apiName}`);
						}

						if (oppositeFields.length === 0) {
							throw new Error(`No primitive field found for opposite relation field ${relationField.apiName}`);
						}

						relations[relationField.apiName] = {
							many: relationField.isList,
							to: {
								[oppositeCollection.apiName]: {
									on: Object.fromEntries(
										oppositeFields
											.map((oppositeField, index) => [oppositeField.apiName, fields[index]!.apiName]),
									),
								},
							},
						};
					}
				}
				catch (error) {
					console.warn(`Failed to resolve relations for collection ${monospaceCollection.apiName}:`, error);
				}
			}

			// Update rstore collection or create a new one
			const existingCollection = store.$collections.find((c) => c.name === monospaceCollection.apiName);

			if (existingCollection) {
				existingCollection.meta ??= {};

				if (!monospaceCollection.isSystem) {
					existingCollection.fields = fieldParsers;
				}

				if (!monospaceCollection.isSystem) {
					Object.assign(existingCollection.meta, meta);
					existingCollection.relations = relations;
					normalizeCollectionRelations([existingCollection]);
					existingCollection.scopeId = 'monospace-default';

					if (getKey) {
						existingCollection.getKey = (item: any) => item.$overrideKey ?? getKey(item);
					}

					patchedRelations = true;
				}
				else {
					Object.assign(existingCollection.meta, {
						relationsWithConstraint,
					});
				}

				continue;
			}

			const storeCollection = defineCollection({
				name: monospaceCollection.apiName,
				fields: fieldParsers,
				scopeId: 'monospace-default',
				relations,
				getKey,
				meta,
			});

			addCollection(store, storeCollection);
		}

		// Re-resolve relations for all collections to they stay in sync
		// after updating relations on existing collections.
		if (patchedRelations) {
			resolveCollectionOppositeRelations(store.$collections);
		}
	}

	function clearWorkspaceCaches() {
		for (const collection of store.$collections) {
			if (collection.meta?.useWorkspaceSlug) {
				store.$cache.clearCollection({ collection });
			}
		}
	}

	function cleanup(currentCollections: ResolvedMonospaceCollection[]) {
		const collectionNames = new Set(currentCollections.map((c) => c.apiName));

		for (const collection of store.$collections) {
			if (collection.meta?.fromWorkspaceCollections && !collectionNames.has(collection.name)) {
				removeCollection(store, collection.name);
			}
		}
	}
});
